import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import FilterChip from '~/components/Shared/Filters/FilterChip/FilterChip';
import { Button } from '~/eds/src/components/Button/Button';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import { Filter } from '~/evifields/src/types/Filter';
import { attachRenderersAndSerializers } from '~/features/advanced-search/filters/Filters.utils';
import { SearchQuery } from '~/features/advanced-search/types';
import { toEntity } from '~/features/advanced-search/utils/filters';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { toSearchV3Filter } from '~/redux/api/methods/searchV3';

import {
  getReadOnlyFiltersForTab,
  getTicketSearchFilters,
  hasId,
} from './TicketFilters.utils';
import { TicketCompletionType } from './workflowTicketTypes';

interface TicketFiltersProps {
  query: SearchQuery;
  onQueryChange: (query: SearchQuery) => void;
  activeTab: TicketCompletionType;
}

export const TicketFilters: React.FC<TicketFiltersProps> = ({
  query,
  onQueryChange,
  activeTab,
}) => {
  const intl = useIntl();
  const currentUser = useCurrentUser();

  const searchFilters = getTicketSearchFilters(intl);
  const fieldsWithLazyLoad = useMemo(() => {
    return searchFilters.map((filter) => {
      if (filter.type === 'enum' || filter.type === 'enum_set') {
        return attachRenderersAndSerializers({
          filters: [filter],
          type: 'ticket',
        })[0];
      }
      return filter;
    });
  }, [searchFilters]);
  // Get read-only filters with user's full name for UI display
  const getReadOnlyQuery = useMemo(
    () =>
      getReadOnlyFiltersForTab(
        activeTab,
        currentUser.id,
        `${currentUser.first_name} ${currentUser.last_name}`,
      ),
    [activeTab, currentUser.id, currentUser.first_name, currentUser.last_name],
  );

  const handleClearFilters = () => {
    onQueryChange([]);
  };

  const allQuery = useMemo(() => {
    const readOnlyFieldIds = new Set(
      getReadOnlyQuery.filter(hasId).map((q) => q.id),
    );
    const userQuery = query.filter(
      (q) => hasId(q) && !readOnlyFieldIds.has(q.id),
    );

    const userFieldIds = new Set(userQuery.filter(hasId).map((q) => q.id));
    const readOnlyFieldIdsSet = new Set(
      getReadOnlyQuery.filter(hasId).map((q) => q.id),
    );

    const emptyFilters = searchFilters
      .filter(
        (sf) => !userFieldIds.has(sf.id) && !readOnlyFieldIdsSet.has(sf.id),
      )
      .map((sf) => ({
        type: 'filter' as const,
        id: sf.id,
        operator: null,
        value: {
          value_list: [],
          value_meta: {},
        },
      }));

    const userSelectableFilters: any[] = [];
    searchFilters.forEach((searchFilter) => {
      const userFilter = userQuery.find(
        (uq) => hasId(uq) && uq.id === searchFilter.id,
      );
      if (userFilter) {
        userSelectableFilters.push(userFilter);
      } else if (!readOnlyFieldIdsSet.has(searchFilter.id)) {
        const emptyFilter = emptyFilters.find(
          (ef) => hasId(ef) && (ef as any).id === searchFilter.id,
        );
        if (emptyFilter) {
          userSelectableFilters.push(emptyFilter);
        }
      }
    });

    // All read-only filters go to the end
    const allReadOnlyFilters = getReadOnlyQuery;

    const allFilters = [...userSelectableFilters, ...allReadOnlyFilters];

    return allFilters;
  }, [query, getReadOnlyQuery, searchFilters, activeTab]);

  const hasUserFilters = query.length > 0;

  return (
    <Layout align="center" spacing={3} p={3}>
      <Text variant="body-bold">
        {intl.formatMessage({
          id: 'TicketFilters.filtersLabel',
          defaultMessage: 'Filters:',
          description: 'Label for filters section',
        })}
      </Text>

      <Layout align="center" spacing={1} wrap>
        {allQuery.map((queryItem, index) => {
          if (!hasId(queryItem)) {
            return null;
          }

          const searchFilter = fieldsWithLazyLoad.find(
            (searchFilter) => String(searchFilter.id) === String(queryItem.id),
          );

          if (!searchFilter) {
            return null;
          }

          const readOnlyFieldIds = getReadOnlyQuery
            .filter(hasId)
            .map((q) => q.id);
          const isReadOnly = readOnlyFieldIds.includes(queryItem.id);

          const searchFiltersRecord = { [searchFilter.id]: searchFilter };

          const filter = toSearchV3Filter(queryItem, searchFiltersRecord);

          return (
            <Layout
              key={`${queryItem.id}_${index}`}
              align="center"
              direction="row"
              mr={1}
              mb={1}
              spacing={0}
              minW={0}
              role="listitem"
              maxW={450}
            >
              <FilterChip
                isPinned={false}
                enableFilterViews={false}
                disableEdit={isReadOnly}
                disableRemove={true}
                readOnly={isReadOnly}
                searchFilter={searchFilter}
                filter={filter}
                hasError={false}
                onChange={(updatedFilter: Filter) => {
                  if (!isReadOnly) {
                    if (updatedFilter.operatorId) {
                      const updatedQueryItem = toEntity(
                        updatedFilter,
                        searchFilter.type,
                      );

                      const existingFilterIndex = query.findIndex(
                        (q) => hasId(q) && q.id === queryItem.id,
                      );

                      let updatedQuery: SearchQuery;
                      if (existingFilterIndex >= 0) {
                        updatedQuery = query.map((q) =>
                          hasId(q) && q.id === queryItem.id
                            ? updatedQueryItem
                            : q,
                        );
                      } else {
                        updatedQuery = [...query, updatedQueryItem];
                      }

                      onQueryChange(updatedQuery);
                    } else {
                      const updatedQuery = query.filter(
                        (q) => !(hasId(q) && q.id === queryItem.id),
                      );
                      onQueryChange(updatedQuery);
                    }
                  }
                }}
              />
            </Layout>
          );
        })}

        <Layout mr={1} mb={1} role="listitem">
          <Button
            iconPosition="left"
            icon="reload"
            variant="action"
            onClick={handleClearFilters}
            disabled={!hasUserFilters}
            text={intl.formatMessage({
              id: 'TicketFilters.resetFilters',
              defaultMessage: 'Reset Filters',
              description: 'Button to reset all filters',
            })}
          />
        </Layout>
      </Layout>
    </Layout>
  );
};
